import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Lock, Loader2, Dumbbell, ExternalLink, AlertCircle, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

interface MembershipStatus {
  status: 'inactive' | 'trial' | 'active' | 'canceled' | 'past_due';
  isBeta: boolean;
  trialEndsAt: string | null;
  subscriptionEndsAt: string | null;
  trialDaysRemaining: number | null;
  stripeCustomerId: string | null;
  trialExpired: boolean;
}

export function Paywall() {
  const { toast } = useToast();
  const { user, signOut } = useAuth();
  
  const { data: membership, isLoading } = useQuery<MembershipStatus>({
    queryKey: ["/api/membership/status"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/membership/status");
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  
  const checkoutMutation = useMutation({
    mutationFn: async () => {
      console.log('[Paywall] Starting checkout...');
      setCheckoutError(null);
      const res = await apiRequest("POST", "/api/membership/checkout");
      const data = await res.json();
      console.log('[Paywall] Checkout response:', data);
      return data;
    },
    onSuccess: (data) => {
      console.log('[Paywall] onSuccess called with:', data);
      if (data.url) {
        console.log('[Paywall] Redirecting to:', data.url);
        // Try redirect - if blocked by iOS, we'll show a manual link
        try {
          window.location.href = data.url;
          // Set timeout to show fallback if redirect didn't happen
          setTimeout(() => {
            setCheckoutUrl(data.url);
          }, 2000);
        } catch (e) {
          console.error('[Paywall] Redirect failed, showing manual link');
          setCheckoutUrl(data.url);
        }
      } else if (data.error) {
        console.error('[Paywall] Server returned error:', data.error);
        setCheckoutError(data.error);
      } else {
        console.error('[Paywall] No URL in response:', data);
        setCheckoutError("Unable to start checkout. Please try again.");
      }
    },
    onError: (error: any) => {
      console.error('[Paywall] Checkout mutation error:', error);
      setCheckoutError(error?.message || "Failed to start checkout. Please try again.");
    },
  });

  if (isLoading || !membership) {
    return null;
  }

  const userRole = (user as any)?.role;
  const userIsCoach = (user as any)?.isCoach;
  const isPrivilegedUser = userRole === 'admin' || userRole === 'coach' || userRole === 'beta' || userIsCoach;
  
  if (membership.isBeta || isPrivilegedUser) {
    return null;
  }

  if (membership.status === 'active' || membership.status === 'trial') {
    return null;
  }

  if (!membership.trialExpired && membership.status !== 'inactive') {
    return null;
  }

  return (
    <div 
      className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 overflow-y-auto"
      data-testid="paywall-overlay"
    >
      <div className="max-w-md w-full bg-card border border-border rounded-2xl p-5 sm:p-8 text-center shadow-2xl my-auto max-h-[calc(100vh-1.5rem)] sm:max-h-[calc(100vh-2rem)] overflow-y-auto">
        <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6 rounded-full bg-primary/10 flex items-center justify-center">
          <Dumbbell className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
        </div>
        
        <h2 className="text-xl sm:text-2xl font-bold mb-2" data-testid="paywall-title">
          Ready to Keep Going?
        </h2>
        
        <p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-6">
          You've been making great progress! Continue your fitness journey with full access to all the tools you love.
        </p>

        <div className="bg-muted/50 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="text-2xl sm:text-3xl font-bold text-primary mb-1">$8.99</div>
          <div className="text-xs sm:text-sm text-muted-foreground">per month</div>
        </div>

        <ul className="text-left text-xs sm:text-sm space-y-2 mb-4 sm:mb-6">
          <li className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
              <svg className="w-3 h-3 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <span>Unlimited workout tracking</span>
          </li>
          <li className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
              <svg className="w-3 h-3 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <span>Progress charts & personal records</span>
          </li>
          <li className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
              <svg className="w-3 h-3 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <span>All workout programs</span>
          </li>
          <li className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
              <svg className="w-3 h-3 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <span>Cross-device sync</span>
          </li>
        </ul>

        {checkoutError && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/30 rounded-lg flex items-start gap-2 text-left">
            <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
            <p className="text-sm text-destructive">{checkoutError}</p>
          </div>
        )}

        {checkoutUrl ? (
          <a
            href={checkoutUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full"
          >
            <Button
              size="lg"
              className="w-full min-h-[48px] text-base"
              data-testid="button-paywall-checkout-link"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Open Checkout
            </Button>
          </a>
        ) : (
          <Button
            size="lg"
            className="w-full min-h-[48px] text-base"
            onClick={() => checkoutMutation.mutate()}
            disabled={checkoutMutation.isPending}
            data-testid="button-paywall-subscribe"
          >
            {checkoutMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Continue My Journey
          </Button>
        )}

        <p className="text-xs text-muted-foreground mt-3 sm:mt-4">
          Cancel anytime, no questions asked. All your workout data is safely saved.
        </p>

        <div className="mt-4 pt-4 border-t border-border">
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground"
            onClick={signOut}
            data-testid="button-paywall-logout"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Log Out
          </Button>
        </div>
      </div>
    </div>
  );
}
